import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "../components/TopBar";
import { StatusBadge } from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatRelative, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { Search, ChevronRight, Building2 } from "lucide-react";

const FALLBACK_GIAVANI_LOGO = "/giavani-default.png";

export default function ThirdPartyPage() {
  const { user } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const r = await api.get("/bath-records", { params });
      setRecords(r.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  // Buckets
  const ready = records.filter((r) => r.status === "awaiting_collection");
  const inHand = records.filter((r) => ["collected", "in_specification"].includes(r.status));
  const readyForReturn = records.filter((r) => r.status === "ready_for_return");
  const returnedOrDone = records.filter((r) => ["returned", "ready_for_dispatch", "dispatched"].includes(r.status));
  const other = records.filter((r) => !["awaiting_collection", "collected", "in_specification", "ready_for_return", "returned", "ready_for_dispatch", "dispatched"].includes(r.status));

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />

      <main className="mx-auto max-w-[1500px] px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <img
              src={settings.giavani_logo_url || FALLBACK_GIAVANI_LOGO}
              alt="Giavani"
              className="h-12 w-12 rounded-md ring-1 ring-zinc-200 object-cover"
            />
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950">
                Giavani — Assignments
              </h1>
              <p className="text-sm text-zinc-600 mt-1">
                Baths assigned to your team for collection, specification and return.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 bg-white border border-zinc-200 px-3 py-2 rounded-md">
            <Building2 className="h-3.5 w-3.5" />
            Signed in as <span className="font-semibold text-zinc-900 ml-1">{user?.name}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Stat label="Awaiting collection" value={ready.length} />
          <Stat label="With you" value={inHand.length} accent="bg-violet-50 border-violet-200" />
          <Stat label="Ready for return" value={readyForReturn.length} accent="bg-fuchsia-50 border-fuchsia-200" />
          <Stat label="Returned / dispatched" value={returnedOrDone.length} accent="bg-emerald-50 border-emerald-200" />
          <Stat label="Total" value={records.length} />
        </div>

        <div className="bg-white border border-zinc-200 rounded-md p-3 mb-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO, SKU, barcode…"
              className="pl-9"
              data-testid="tp-search"
            />
          </div>
        </div>

        <Bucket title="Ready for collection" records={ready} onOpen={(id) => navigate(`/bath/${id}`)} testid="tp-bucket-ready" />
        <Bucket title="With your team" records={inHand} onOpen={(id) => navigate(`/bath/${id}`)} testid="tp-bucket-inhand" />
        <Bucket title="Returned / completed" records={returnedOrDone} onOpen={(id) => navigate(`/bath/${id}`)} testid="tp-bucket-done" />
        {other.length > 0 && (
          <Bucket title="Other" records={other} onOpen={(id) => navigate(`/bath/${id}`)} testid="tp-bucket-other" />
        )}
        {!loading && records.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-10 text-center text-sm text-zinc-500">
            No bath assignments yet.
          </div>
        )}
      </main>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className={`border rounded-md p-4 shadow-sm ${accent || "bg-white border-zinc-200"}`}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
    <div className="font-heading text-3xl font-extrabold tracking-tighter text-zinc-950 mt-1">{value}</div>
  </div>
);

const Bucket = ({ title, records, onOpen, testid }) => {
  if (records.length === 0) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden mb-4" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
        <h3 className="font-heading font-bold text-sm tracking-tight text-zinc-950">{title}</h3>
        <span className="text-xs text-zinc-500">{records.length}</span>
      </div>
      <ul>
        {records.map((r) => (
          <li
            key={r.id}
            onClick={() => onOpen(r.id)}
            className="border-t border-zinc-100 px-4 py-3 hover:bg-zinc-50 cursor-pointer flex items-center justify-between gap-3 first:border-t-0"
            data-testid={`tp-row-${r.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-mono text-xs font-semibold text-zinc-950">{r.supplier_po}</span>
                <span className="font-mono text-xs text-zinc-500">{r.internal_po}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="text-xs text-zinc-700 truncate">{r.description}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {r.location || "—"} · Updated {formatRelative(r.updated_at)}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-zinc-400" />
          </li>
        ))}
      </ul>
    </div>
  );
};
